<script lang="ts">
  import SpinWheel from './components/SpinWheel.svelte';
  import Header from './components/Header.svelte';
  import Footer from './components/Footer.svelte';
  import AdSenseBanner from './components/AdSenseBanner.svelte';
  import ChessGame from './components/ChessGame.svelte';
  import LudoGame from './components/LudoGame.svelte';
  import BlackjackGame from './components/BlackjackGame.svelte';
  import Leaderboard from './components/Leaderboard.svelte';
  
  // Game state
  let currentView = $state('home'); // home, chess, ludo, blackjack, leaderboard
  let unlockedGames = $state(['chess']); // Games unlocked by spinning
  let gamePlays = $state(3); // Number of game plays available
  let consecutiveWins = $state(0);
  let playerScore = $state(0);
  let timeSpent = $state(0);
  let currentPlayerName = $state(localStorage.getItem('playerName') || 'Guest');
  
  // Track time spent
  $effect(() => {
    const timer = setInterval(() => {
      timeSpent += 1;
    }, 60000); // Every minute
    
    return () => clearInterval(timer);
  });
  
  function handleWin(prize: string) {
    if (prize.includes('Game Play')) {
      gamePlays += 1;
    } else if (prize.includes('Unlock')) {
      if (prize.includes('Ludo') && !unlockedGames.includes('ludo')) {
        unlockedGames = [...unlockedGames, 'ludo'];
      } else if (prize.includes('Blackjack') && !unlockedGames.includes('blackjack')) {
        unlockedGames = [...unlockedGames, 'blackjack'];
      }
    }
  }
  
  function playGame(game: string) {
    if (gamePlays > 0 && unlockedGames.includes(game)) {
      gamePlays -= 1;
      currentView = game;
    }
  }
  
  function handleGameWin(points: number) {
    consecutiveWins += 1;
    playerScore += points;
    // Reward with extra game play on win
    if (consecutiveWins % 3 === 0) {
      gamePlays += 1;
    }
  }
  
  function handleGameLoss() {
    consecutiveWins = 0;
  }
  
  function backToHome() {
    currentView = 'home';
  }
  
  function savePlayerName(name: string) {
    currentPlayerName = name;
    localStorage.setItem('playerName', name);
  }
</script>

<main class="app-container">
  <Header />
  
  {#if currentView === 'home'}
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">
          🌿 <span class="gradient-text">Grn Haus Games</span> 🎮
        </h1>
        <p class="hero-subtitle">
          Eco-Friendly Gaming for World Cup 2026! 🏆<br/>
          Spin to unlock 3 exciting games: Chess, Ludo & Blackjack
        </p>
        
        <div class="stats-bar">
          <div class="stat-item">
            <span class="stat-icon">🎯</span>
            <span class="stat-value">{gamePlays}</span>
            <span class="stat-label">Game Plays</span>
          </div>
          <div class="stat-item">
            <span class="stat-icon">🏆</span>
            <span class="stat-value">{playerScore}</span>
            <span class="stat-label">Score</span>
          </div>
          <div class="stat-item">
            <span class="stat-icon">🔥</span>
            <span class="stat-value">{consecutiveWins}</span>
            <span class="stat-label">Win Streak</span>
          </div>
          <div class="stat-item">
            <span class="stat-icon">⏱️</span>
            <span class="stat-value">{timeSpent}m</span>
            <span class="stat-label">Time Played</span>
          </div>
        </div>
        
        <button class="spin-cta-btn" onclick={() => document.getElementById('wheel-section')?.scrollIntoView({ behavior: 'smooth' })}>
          🎰 SPIN TO PLAY - IT'S FREE!
        </button>
        <p class="green-vibes">♻️ Green Friendly • 🌍 Climate Aware • 🎮 World Cup 2026 Ready</p>
      </div>
    </section>
    
    <AdSenseBanner position="top" />
    
    <!-- Games Selection -->
    <section id="games" class="games-section">
      <h2>🎮 Choose Your Game</h2>
      <div class="games-grid">
        <!-- Chess Card -->
        <div class="game-card {unlockedGames.includes('chess') ? 'unlocked' : 'locked'}" 
             onclick={() => playGame('chess')}>
          <div class="game-icon">♟️</div>
          <h3>3D Chess</h3>
          <p>Classic strategy game in 3D</p>
          <div class="difficulty-indicator">
            <span>Difficulty:</span>
            <div class="level-bars">
              <div class="level-bar"></div>
              <div class="level-bar"></div>
              <div class="level-bar"></div>
            </div>
          </div>
          {#if !unlockedGames.includes('chess')}
            <div class="lock-overlay">🔒 Spin to Unlock</div>
          {/if}
        </div>
        
        <!-- Ludo Card -->
        <div class="game-card {unlockedGames.includes('ludo') ? 'unlocked' : 'locked'}" 
             onclick={() => playGame('ludo')}>
          <div class="game-icon">🎲</div>
          <h3>Ludo / Snakes</h3>
          <p>Classic board game fun</p>
          <div class="difficulty-indicator">
            <span>Difficulty:</span>
            <div class="level-bars">
              <div class="level-bar"></div>
              <div class="level-bar"></div>
            </div>
          </div>
          {#if !unlockedGames.includes('ludo')}
            <div class="lock-overlay">🔒 Spin to Unlock</div>
          {:else}
            <div class="play-badge">▶️ Play</div>
          {/if}
        </div>
        
        <!-- Blackjack Card -->
        <div class="game-card {unlockedGames.includes('blackjack') ? 'unlocked' : 'locked'}" 
             onclick={() => playGame('blackjack')}>
          <div class="game-icon">🃏</div>
          <h3>Blackjack</h3>
          <p>Beat the dealer to 21</p>
          <div class="difficulty-indicator">
            <span>Difficulty:</span>
            <div class="level-bars">
              <div class="level-bar"></div>
              <div class="level-bar"></div>
              <div class="level-bar"></div>
              <div class="level-bar"></div>
            </div>
          </div>
          {#if !unlockedGames.includes('blackjack')}
            <div class="lock-overlay">🔒 Spin to Unlock</div>
          {:else}
            <div class="play-badge">▶️ Play</div>
          {/if}
        </div>
      </div>
    </section>
    
    <!-- Spin Wheel Section -->
    <section id="wheel-section" class="wheel-section">
      <h2>🎰 Spin to Unlock Games & Win Plays!</h2>
      <p class="wheel-subtitle">Each spin can unlock new games or give you free game plays!</p>
      <SpinWheel onWin={(e) => handleWin(e.detail)} />
    </section>
    
    <AdSenseBanner position="middle" />
    
    <!-- Leaderboard Section -->
    <section id="leaderboard" class="leaderboard-section">
      <Leaderboard currentPlayer={currentPlayerName} currentScore={playerScore} />
    </section>
    
    <!-- Features Section -->
    <section class="features-section" id="about">
      <h2>🌿 Why Grn Haus Games?</h2>
      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon">🌍</div>
          <h3>Eco-Friendly</h3>
          <p>Green-themed gaming promoting environmental awareness for World Cup 2026</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">📈</div>
          <h3>Progressive Difficulty</h3>
          <p>Start easy, get challenged! Games get harder as you level up</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">🏆</div>
          <h3>Real Leaderboard</h3>
          <p>Compete globally! Climb the ranks with consecutive wins</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">♻️</div>
          <h3>Recycle Friendly</h3>
          <p>Win rewards that recycle back into more gameplay opportunities</p>
        </div>
      </div>
    </section>
    
    <AdSenseBanner position="bottom" />
    
  {:else if currentView === 'chess'}
    <ChessGame 
      onBack={backToHome} 
      onWin={(e) => handleGameWin(e.detail)}
      onLoss={handleGameLoss}
      playerName={currentPlayerName}
    />
  {:else if currentView === 'ludo'}
    <LudoGame 
      onBack={backToHome}
      onWin={(e) => handleGameWin(e.detail)}
      onLoss={handleGameLoss}
      playerName={currentPlayerName}
    />
  {:else if currentView === 'blackjack'}
    <BlackjackGame 
      onBack={backToHome}
      onWin={(e) => handleGameWin(e.detail)}
      onLoss={handleGameLoss}
      playerName={currentPlayerName}
    />
  {/if}
  
  <Footer />
</main>

<style>
  :global(*) {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  :global(body) {
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: linear-gradient(135deg, #d4edda 0%, #a8e6cf 50%, #7fdbb6 100%);
    min-height: 100vh;
    color: #2d5016;
    overflow-x: hidden;
  }
  
  .app-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
  }
  
  .hero-section {
    text-align: center;
    padding: 120px 20px 60px;
    background: linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(39, 174, 96, 0.1) 100%);
    border-radius: 30px;
    margin-top: 20px;
  }
  
  .hero-title {
    font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-weight: 900;
    margin-bottom: 20px;
    text-shadow: 3px 3px 6px rgba(46, 204, 113, 0.3);
  }
  
  .gradient-text {
    background: linear-gradient(45deg, #2ecc71, #27ae60, #1abc9c, #16a085);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% { filter: brightness(1); }
    50% { filter: brightness(1.2); }
  }
  
  .hero-subtitle {
    font-size: clamp(1.1rem, 3vw, 1.5rem);
    margin-bottom: 30px;
    color: #2d5016;
    line-height: 1.8;
  }
  
  .stats-bar {
    display: flex;
    justify-content: center;
    gap: 20px;
    flex-wrap: wrap;
    margin: 30px 0;
    padding: 20px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    box-shadow: 0 10px 30px rgba(46, 204, 113, 0.2);
  }
  
  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 15px 25px;
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    border-radius: 15px;
    color: white;
    min-width: 100px;
    transition: all 0.3s ease;
  }
  
  .stat-item:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(46, 204, 113, 0.4);
  }
  
  .stat-icon {
    font-size: 1.8rem;
    margin-bottom: 5px;
  }
  
  .stat-value {
    font-size: 1.8rem;
    font-weight: 800;
  }
  
  .stat-label {
    font-size: 0.85rem;
    opacity: 0.9;
  }
  
  .spin-cta-btn {
    background: linear-gradient(45deg, #2ecc71, #27ae60, #1abc9c);
    color: white;
    border: none;
    padding: 25px 60px;
    font-size: 1.5rem;
    font-weight: 800;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 10px 40px rgba(46, 204, 113, 0.4);
    text-transform: uppercase;
    letter-spacing: 2px;
    margin: 20px 0;
    animation: pulse-glow 2s ease-in-out infinite;
  }
  
  @keyframes pulse-glow {
    0%, 100% { 
      box-shadow: 0 10px 40px rgba(46, 204, 113, 0.4);
      transform: scale(1);
    }
    50% { 
      box-shadow: 0 15px 50px rgba(46, 204, 113, 0.6);
      transform: scale(1.02);
    }
  }
  
  .spin-cta-btn:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 20px 60px rgba(46, 204, 113, 0.6);
  }
  
  .green-vibes {
    margin-top: 20px;
    font-size: 1rem;
    color: #27ae60;
    font-weight: 600;
  }
  
  .games-section {
    padding: 60px 20px;
    text-align: center;
  }
  
  .games-section h2 {
    font-size: 2.5rem;
    color: #2d5016;
    margin-bottom: 40px;
    text-shadow: 2px 2px 4px rgba(46, 204, 113, 0.2);
  }
  
  .games-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 30px;
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .game-card {
    background: linear-gradient(135deg, #ffffff 0%, #f0fff4 100%);
    border-radius: 25px;
    padding: 40px 30px;
    text-align: center;
    cursor: pointer;
    transition: all 0.4s ease;
    border: 3px solid transparent;
    position: relative;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(46, 204, 113, 0.15);
  }
  
  .game-card.unlocked:hover {
    transform: translateY(-15px);
    border-color: #2ecc71;
    box-shadow: 0 20px 50px rgba(46, 204, 113, 0.3);
  }
  
  .game-card.locked {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .game-icon {
    font-size: 5rem;
    margin-bottom: 20px;
    display: block;
  }
  
  .game-card h3 {
    font-size: 1.8rem;
    color: #2d5016;
    margin-bottom: 10px;
  }
  
  .game-card p {
    color: #5a7a5a;
    margin-bottom: 20px;
  }
  
  .difficulty-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 15px;
  }
  
  .level-bars {
    display: flex;
    gap: 5px;
  }
  
  .level-bar {
    width: 20px;
    height: 8px;
    background: linear-gradient(90deg, #2ecc71, #27ae60);
    border-radius: 4px;
  }
  
  .lock-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(46, 204, 113, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    font-weight: 700;
    color: white;
    border-radius: 25px;
  }
  
  .play-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    background: linear-gradient(45deg, #2ecc71, #27ae60);
    color: white;
    padding: 8px 15px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 700;
  }
  
  .wheel-section {
    padding: 60px 20px;
    text-align: center;
    background: linear-gradient(135deg, rgba(46, 204, 113, 0.05) 0%, rgba(39, 174, 96, 0.05) 100%);
    border-radius: 30px;
    margin: 40px 0;
  }
  
  .wheel-section h2 {
    font-size: 2.5rem;
    color: #2d5016;
    margin-bottom: 15px;
  }
  
  .wheel-subtitle {
    color: #5a7a5a;
    font-size: 1.1rem;
    margin-bottom: 30px;
  }
  
  .leaderboard-section {
    padding: 60px 20px;
  }
  
  .features-section {
    background: white;
    border-radius: 30px;
    padding: 60px 30px;
    margin: 40px 0;
    box-shadow: 0 15px 50px rgba(46, 204, 113, 0.15);
  }
  
  .features-section h2 {
    text-align: center;
    font-size: 2.5rem;
    color: #2d5016;
    margin-bottom: 50px;
  }
  
  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;
  }
  
  .feature-card {
    text-align: center;
    padding: 35px 25px;
    border-radius: 20px;
    background: linear-gradient(135deg, #f0fff4 0%, #d4edda 100%);
    transition: all 0.3s ease;
    border: 2px solid transparent;
  }
  
  .feature-card:hover {
    transform: translateY(-10px);
    border-color: #2ecc71;
    box-shadow: 0 15px 40px rgba(46, 204, 113, 0.25);
  }
  
  .feature-icon {
    font-size: 3.5rem;
    margin-bottom: 20px;
  }
  
  .feature-card h3 {
    font-size: 1.5rem;
    color: #2d5016;
    margin-bottom: 12px;
  }
  
  .feature-card p {
    color: #5a7a5a;
    line-height: 1.7;
  }
  
  @media (max-width: 768px) {
    .hero-section {
      padding: 100px 15px 40px;
    }
    
    .spin-cta-btn {
      padding: 20px 40px;
      font-size: 1.2rem;
    }
    
    .stats-bar {
      gap: 10px;
    }
    
    .stat-item {
      min-width: 80px;
      padding: 10px 15px;
    }
    
    .games-section,
    .wheel-section,
    .features-section {
      padding: 40px 15px;
    }
    
    .games-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
